import { memo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack } from "@mui/material";
import { IconCaret } from "src/components/icons-mui/caret";
import { Typography } from "src/components-mui/base/typography/typography";
import { PerformanceValue } from "src/components-mui/common/performance-value/performance-value";
import { UnderlyingLogo } from "src/components-mui/common/underlying-logo/underlying-logo";
import { ListRowDetails } from "src/components-mui/modules/underlying-lists-module/components/list-row-details";
import {
    UnderlyingListMotiveColor,
    UnderlyingListMotive,
} from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.constants";
import { IUnderlyingListItem } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.types";

interface IListRowProps {
    item: IUnderlyingListItem;
    daysCount: number;
    motiveColor: UnderlyingListMotiveColor;
    motive: UnderlyingListMotive;
    isExpanded: boolean;
    isDisabled: boolean;
    isLast: boolean;
    onItemToggle: ({ motive, isin }: { motive: UnderlyingListMotive; isin: string }) => void;
}

const ListRowComponent = ({ item, daysCount, motiveColor, motive, isExpanded, isDisabled, isLast, onItemToggle }: IListRowProps) => {
    return (
        <Box component="li">
            <Accordion
                expanded={isExpanded}
                onChange={() => onItemToggle({ motive, isin: item.isin })}
                disabled={isDisabled}
                slots={{ heading: "div" }}
                slotProps={{ transition: { timeout: 200, unmountOnExit: true } }}
                sx={{
                    borderBottom: isLast ? "2px solid" : "1px solid",
                    borderColor: isLast ? `${motiveColor}.500` : "gray.100",
                    "&.Mui-disabled": { backgroundColor: "transparent" },
                }}
            >
                <AccordionSummary
                    expandIcon={<IconCaret sx={{ width: "24px", height: "24px" }} />}
                    sx={{
                        alignItems: "center",
                        minHeight: 0,
                        px: 2,
                        "& .MuiAccordionSummary-expandIconWrapper": {
                            justifyContent: "center",
                            alignItems: "center",
                        },
                        pt: { xs: "12px", sm: "12px" },
                        pb: isLast ? { xs: "12px", sm: "12px" } : { xs: "14px", sm: "12px" },
                        "&.Mui-disabled": { opacity: 1 },
                        "& .MuiAccordionSummary-content": { alignItems: "center", minWidth: 0 },
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            alignItems: "center",
                            gap: 2,
                            fontSize: { xs: "sm", sm: "md" },
                        }}
                    >
                        <UnderlyingLogo logoUrl={item.logoUrl} size="sm" sx={{ display: { xs: "none", sm: "flex" }, flexShrink: 0 }} />
                        <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: "inherit", overflowWrap: "anywhere" }}>
                            {item.name}
                        </Typography>
                        <PerformanceValue value={item.priceChange} sx={{ flexShrink: 0, fontSize: "inherit", textAlign: "right" }} />
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pl: { xs: 2, sm: 9 }, pr: 6, pt: 0.5, pb: 2, fontSize: { xs: "sm", sm: "md" } }}>
                    <ListRowDetails item={item} daysCount={daysCount} />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export const ListRow = memo(ListRowComponent);
